import { Plus, X } from "lucide-react";
import { type KeyboardEvent, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { Command } from "./Command";

export interface TagInputProps {
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Offered below the box while typing; already-chosen values are hidden. */
  suggestions?: readonly string[];
  placeholder?: string;
  size?: "sm" | "md";
  /** Cleans typed text before it becomes a chip. Returning `""` drops the entry. Default: trim. */
  normalize?: (text: string) => string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  className?: string;
}

const defaultNormalize = (text: string): string => text.trim();

function sameKey(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * A chip input. Enter or comma commits the typed text as a chip, Backspace on an empty box
 * removes the last chip, and blur commits whatever was typed. While typing, matching
 * `suggestions` (plus an "Add" entry for new text) appear in a list: arrows move, Enter picks.
 * The component is generic; callers own the vocabulary through `normalize` and `suggestions`.
 */
export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder,
  size = "md",
  normalize = defaultNormalize,
  disabled = false,
  autoFocus,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  className,
}: TagInputProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (raw: string) => {
    const tag = normalize(raw);
    setQuery("");
    if (tag === "" || value.some((v) => sameKey(v, tag))) return;
    onChange([...value, tag]);
  };
  const remove = (tag: string) => onChange(value.filter((v) => v !== tag));

  const q = query.trim();
  const matches =
    q === ""
      ? []
      : suggestions
          .filter((s) => !value.some((v) => sameKey(v, s)))
          .filter((s) => s.toLowerCase().includes(q.toLowerCase()))
          .sort((a, b) => {
            const pa = a.toLowerCase().startsWith(q.toLowerCase()) ? 0 : 1;
            const pb = b.toLowerCase().startsWith(q.toLowerCase()) ? 0 : 1;
            return pa - pb;
          });
  const exact = matches.some((s) => sameKey(s, q)) || value.some((v) => sameKey(v, q));
  const listOpen = q !== "" && (matches.length > 0 || !exact);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    switch (event.key) {
      case ",":
        event.preventDefault();
        event.stopPropagation();
        add(query);
        return;
      case "Enter":
        // With the list open the surrounding Command picks the active item.
        if (listOpen) return;
        event.preventDefault();
        event.stopPropagation();
        add(query);
        return;
      case "Backspace":
        if (query === "" && value.length > 0) {
          event.preventDefault();
          remove(value[value.length - 1]!);
        }
        return;
      case "Escape":
        if (query !== "") {
          event.preventDefault();
          event.stopPropagation();
          setQuery("");
        }
        return;
      case "ArrowDown":
      case "ArrowUp":
        if (!listOpen) event.stopPropagation();
        return;
      case "Home":
      case "End":
        // Text navigation, never list navigation.
        event.stopPropagation();
        return;
      default:
    }
  };

  const small = size === "sm";

  return (
    <Command.Root
      value={query}
      onValueChange={setQuery}
      data-slot="tag-input"
      data-size={size}
      className={cn("relative h-auto overflow-visible bg-transparent", className)}
    >
      {/* The box is a click target that forwards focus; the input inside is the control. */}
      <div
        data-slot="tag-input-box"
        onClick={() => inputRef.current?.focus()}
        className={cn(
          "flex w-full min-w-0 cursor-text flex-wrap items-center gap-1 rounded-md border border-border bg-bg field-focus hover:border-border-strong",
          small ? "min-h-7 px-1.5 py-0.5" : "min-h-8 px-2 py-1",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        {value.map((tag) => (
          <span
            key={tag}
            data-slot="tag-input-chip"
            className={cn(
              "inline-flex items-center gap-0.5 rounded-sm bg-fg/8 pl-1.5 font-medium text-fg whitespace-nowrap",
              small ? "h-4 text-[11px]" : "h-5 text-xs",
            )}
          >
            {tag}
            <button
              type="button"
              tabIndex={-1}
              aria-label={`Remove ${tag}`}
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                remove(tag);
                inputRef.current?.focus();
              }}
              className="inline-flex h-full items-center rounded-r-sm px-0.5 text-fg-muted outline-none hover:bg-fg/8 hover:text-fg"
            >
              <X className={small ? "size-2.5" : "size-3"} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (query.trim() !== "") add(query);
          }}
          placeholder={value.length === 0 ? placeholder : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-autocomplete="list"
          aria-expanded={listOpen}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={autoFocus}
          disabled={disabled}
          className={cn(
            "min-w-16 flex-1 bg-transparent text-fg outline-none placeholder:text-fg-faint disabled:cursor-not-allowed",
            small ? "h-5 text-xs" : "h-6 text-sm",
          )}
        />
      </div>
      {listOpen && (
        <Command.List
          data-slot="tag-input-suggestions"
          className="absolute top-full right-0 left-0 z-dropdown mt-1 max-h-56 rounded-md border border-border bg-surface-raised shadow-md"
        >
          {!exact && (
            <Command.Item
              value="__add__"
              onSelect={() => add(q)}
              onMouseDown={(event) => event.preventDefault()}
              className="py-1"
            >
              <Plus />
              Add “{normalize(q) || q}”
            </Command.Item>
          )}
          {matches.map((tag) => (
            <Command.Item
              key={tag}
              value={tag}
              onSelect={() => add(tag)}
              onMouseDown={(event) => event.preventDefault()}
              className="py-1"
            >
              {tag}
            </Command.Item>
          ))}
        </Command.List>
      )}
    </Command.Root>
  );
}
